<template>
  <div class="notifications-list">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="mb-0">{{ $t("notifications") }}</h2>
      <b-button v-if="hasUnreadNotifications" variant="primary" @click="markAllAsRead">
        <i class="fas fa-check mr-2"></i>{{ $t("mark_all_as_read") }}
      </b-button>
    </div>

    <CrudTable
      :columns="fields"
      :data-sources="notifications"
      :pagination="paginationInfo"
      :loader="loading"
      list-url="/notifications"
      @search="handleSearch"
      @filter="handleFilter"
      @refresh="fetchNotifications"
    >
      <!-- Message Column -->
      <template #cell(message)="data">
        <div class="d-flex align-items-center">
          <i :class="getNotificationIcon(data.item.action_type)" class="me-3 fs-4"></i>
          <div class="message-content">
            <div
              class="message-text"
              v-html="formatMessageWithLinks(data.item.message)"
            ></div>
            <div class="text-muted small">{{ data.item.send_to || "N/A" }}</div>
          </div>
        </div>
      </template>

      <!-- Type Column -->
      <template #cell(action_type)="data">
        <span class="badge" :class="getTypeBadgeClass(data.item.action_type)">
          {{ data.item.action_type || "N/A" }}
        </span>
      </template>

      <!-- Date Column -->
      <template #cell(date)="data">
        <div>
          {{ data.item.date || "N/A" }}
        </div>
      </template>

      <!-- Time Column -->
      <template #cell(time)="data">
        <div>
          {{ data.item.time || "N/A" }}
        </div>
      </template>

      <!-- Status Column -->
      <template #cell(read)="data">
        <span class="badge" :class="data.item.read ? 'bg-success' : 'bg-warning'">
          {{ data.item.read ? $t("read") : $t("unread") }}
        </span>
      </template>

      <!-- Actions Column -->
      <template #cell(actions)="data">
        <button
          v-if="!data.item.read"
          @click="markAsRead(data.item)"
          class="btn btn-link btn-sm text-decoration-none"
        >
          {{ $t("mark_as_read") }}
        </button>
      </template>
    </CrudTable>
  </div>
</template>

<script>
import { request } from "@/Util/Request";
import CrudTable from "@/components/CrudTable.vue";
import { formatMessageWithLinks } from "@/Util/MessageFormatter";

export default {
  name: "NotificationsList",
  components: {
    CrudTable,
  },
  data() {
    return {
      authError: false,
      notifications: [],
      loading: false,
      paginationInfo: {
        total: 0,
        per_page: 10,
        current_page: 1,
      },
      fields: [
        {
          key: "message",
          label: this.$t("message"),
          sortable: true,
          thStyle: { width: "60%" },
          tdClass: "message-cell",
        },
        {
          key: "action_type",
          label: this.$t("type"),
          sortable: true,
          width: "12%",
        },
        {
          key: "date",
          label: this.$t("date"),
          sortable: true,
          width: "12%",
        },
        {
          key: "time",
          label: this.$t("time"),
          sortable: true,
          width: "12%",
        },
        {
          key: "read",
          label: this.$t("status"),
          sortable: true,
          width: "8%",
        },
        {
          key: "actions",
          label: this.$t("actions"),
          width: "6%",
        },
      ],
    };
  },
  computed: {
    hasUnreadNotifications() {
      if(this.notifications){
        return this.notifications.some((notification) => !notification.read);
      }
      return false;
    },
  },
  methods: {
    async fetchNotifications() {
      this.loading = true;
      try {
        const response = await request({
          method: "get",
          url: `/notifications`,
        });
        this.notifications = response.data?response.data.data:[];
        // Update pagination info if available in response
        if (response.data.meta) {
          this.paginationInfo = {
            total: response.data.meta.total,
            per_page: response.data.meta.per_page,
            current_page: response.data.meta.current_page,
          };
        }
      } catch (error) {
        this.authError = true;
        console.error("Error fetching notifications:", error);
      } finally {
        this.loading = false;
      }
    },
    async markAsRead(notification) {
      try {
        console.log("Marking notification as read:", notification);
        await request({
          method: "put",
          url: `/notifications/${notification.id}/read`,
        });
        notification.read = true;
      } catch (error) {
        console.error("Error marking notification as read:", error);
      }
    },
    async markAllAsRead() {
      try {
        await request({
          method: "put",
          url: "/notifications/mark-all-read",
        });
        this.notifications.forEach((notification) => {
          notification.read = true;
        });
      } catch (error) {
        console.error("Error marking all notifications as read:", error);
      }
    },
    getNotificationIcon(type) {
      const icons = {
        sms: "bi bi-envelope",
        email: "bi bi-envelope",
        system: "bi bi-gear",
        default: "bi bi-bell",
      };
      return icons[type] || icons.default;
    },
    getTypeBadgeClass(type) {
      const classes = {
        sms: "bg-info",
        email: "bg-primary",
        system: "bg-secondary",
        default: "bg-light text-dark",
      };
      return classes[type] || classes.default;
    },
    handleSearch(searchTerm) {
      // Implement search functionality if needed
      console.log("Search:", searchTerm);
    },
    handleFilter(filters) {
      // Implement filter functionality if needed
      console.log("Filters:", filters);
    },
    formatMessageWithLinks,
  },
  mounted() {
    this.fetchNotifications();
  },
};
</script>

<style scoped>
.notifications-list {
  padding: 1rem;
}

.message-content {
  flex: 1;
  min-width: 0;
  max-width: 100%;
}

:deep(.message-cell) {
  width: 60% !important;
  max-width: 60% !important;
}

.message-text {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.4;
}

:deep(.message-link) {
  color: #007bff;
  text-decoration: none;
  padding: 2px 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

:deep(.message-link:hover) {
  color: #0056b3;
  text-decoration: underline;
}

.badge {
  font-weight: 500;
  padding: 0.5em 0.75em;
}

.btn-link {
  padding: 0;
  color: var(--bs-primary);
}

.btn-link:hover {
  color: var(--bs-primary-dark);
}
</style>
